// Locale-formatted <-> parsed-number conversion for the MapLayer admin form's Bounds/coordinate
// inputs (Views/Settings/MapLayer.cshtml). Those inputs render and bind server-side per
// CultureInfo.CurrentCulture (repo convention: numbers parse strictly per CurrentCulture, store
// invariant, render CurrentCulture -- see CLAUDE.md), so any value client script writes into them
// must match that convention, and any value it reads back must be parsed the same way the server
// would parse it. This module is the one place that logic lives; it is consumed via
// window.GSatCoordinateFormat (map.mjs) so every crossing point applies the identical rules.
//
// numberFormat is the { decimalSeparator, groupSeparator } shape published as
// window.GSatNumberFormat (Views/Shared/_LayoutBetaCore.cshtml), sourced from
// CultureInfo.CurrentCulture.NumberFormat. Both functions tolerate a missing/null numberFormat by
// falling back to invariant ('.', no grouping).

const INVARIANT_FORMAT = { decimalSeparator: ".", groupSeparator: "" };

// Text already in invariant/period-decimal form (a bare integer, or a period-decimal number such
// as Number.prototype.toFixed's output) skips separator handling entirely and goes straight to
// parseFloat. formatCoordinate below always writes CurrentCulture now, so this fast path is
// usually redundant, but it defends against any other writer that still emits invariant text, and
// costs nothing -- do not delete this as dead code. It is the fix for a regression that already
// shipped once (see the regression test in coordinate-format.test.mjs): in a locale whose GROUP
// separator is '.' (de, es/es-ES, it, pt-BR, vi-VN), running "52.900000" through the
// group-then-decimal normalization below -- stripping '.' as if it were grouping -- eats the
// decimal point and produces 52900000. A single '.' followed by digits can't be mistaken for real
// thousands grouping in any affected locale (grouping happens in threes, "52.900.000"), and
// coordinates are bounded to +/-180 so no legitimately grouped value can appear here anyway.
const INVARIANT_NUMBER_RE = /^-?\d+(\.\d+)?$/;

// Parses coordinate text -- CurrentCulture-formatted or already-invariant -- to a number, or NaN
// when the text is blank, whitespace-only, or otherwise unparseable.
export function parseCoordinate(value, numberFormat) {
	const text = String(value == null ? "" : value).trim();
	if (INVARIANT_NUMBER_RE.test(text)) {
		return parseFloat(text);
	}
	const format = numberFormat || INVARIANT_FORMAT;
	let normalized = text;
	if (format.groupSeparator) {
		normalized = normalized.split(format.groupSeparator).join("");
	}
	if (format.decimalSeparator && format.decimalSeparator !== ".") {
		normalized = normalized.split(format.decimalSeparator).join(".");
	}
	return parseFloat(normalized);
}

// Formats a coordinate to `decimals` places in CurrentCulture, e.g.
// formatCoordinate(52.9, 6, { decimalSeparator: ",", groupSeparator: "." }) -> "52,900000". Never
// emits a group separator: coordinates are bounded to +/-180, so grouping never legitimately
// applies, and emitting one would recreate the ambiguity parseCoordinate's invariant fast path
// above exists to defend against. toFixed first (always period-decimal, never grouped), then swap
// '.' for decimalSeparator -- nothing else.
export function formatCoordinate(value, decimals, numberFormat) {
	const text = Number(value).toFixed(decimals);
	const format = numberFormat || INVARIANT_FORMAT;
	if (!format.decimalSeparator || format.decimalSeparator === ".") {
		return text;
	}
	return text.split(".").join(format.decimalSeparator);
}
